import i18n from 'locales/fa.json';
import {
  convertToJalaliDate,
  loadAllReportDates,
  loadAllScenarios,
} from './common.util';
import {
  AnalysisType,
  CommoditiesDataType,
  CommodityItemType,
  FundamentalDataType,
  FundamentalFormulaResponseType,
  FundamentalFormulaType,
  FundamentalResponseType,
  FundamentalSummaryType,
  ImportantCommodityType,
} from 'typings/fundamental.type';

// we should be separate macro parameters and important commodities
export const getCommodities = (
  data: ImportantCommodityType,
  id: number,
): CommoditiesDataType => {
  const result = {
    macroParameters: [],
    importantCommodities: [],
  };
  Object.keys(data).forEach((key) => {
    const resultKey = data[key].is_macro_parameter
      ? 'macroParameters'
      : 'importantCommodities';
    result[resultKey].push({
      name: data[key].commodity,
      unit: data[key].unit,
      value:
        data[key].unit === i18n.global.percent
          ? data[key].calculated_value * 100
          : data[key].calculated_value,
      isMacro: data[key].is_macro_parameter,
      slug: key.replace(i18n.fundamental.SummaryOfAnalysis, ''),
      id,
    });
  });

  return result;
};

export const FUNDAMENTAL_INITIAL_DATA = {
  summary: null,
  tableData: { macroParameters: [], importantCommodities: [] },
};

export const replacePrice = (
  data: FundamentalDataType,
  values: FundamentalFormulaType,
  isMacro: boolean,
): CommodityItemType[] =>
  data.tableData[isMacro ? 'macroParameters' : 'importantCommodities'].map(
    (item) =>
      item.slug === values.slug
        ? { ...item, hasChange: true, value: values.value }
        : item,
  );

export const updateEps = (
  data: FundamentalDataType,
  values: FundamentalFormulaType,
  response: FundamentalFormulaResponseType,
): FundamentalDataType => {
  const tableData = {
    importantCommodities: replacePrice(data, values, false),
    macroParameters: replacePrice(data, values, true),
  };
  return {
    summary: {
      ...data.summary,
      earningPerShare: response.earning_per_share,
      pricePerEps: response.price_per_eps,
    },
    tableData,
  };
};

export const summaryNormalizer = (
  data: FundamentalResponseType,
): FundamentalSummaryType => ({
  id: data.id,
  fundamentalId: data.id,
  symbol: data.security_symbol,
  isDefault: data.is_default,
  security: data.security,
  symbol12Chars: data.security_symbol_12chars,
  securityName: data.security_name,
  scenarioName: data.scenario_name,
  allScenarios: loadAllScenarios(data),
  allReportDates: loadAllReportDates(data),
  reportDate: convertToJalaliDate(data.summary.report_date),
  lastPrice: data.summary.price,
  status: data.summary.status,
  lastPricePercentageChange: data.summary.change_price_percentage.toFixed(2),
  pricePerSales: data.price_per_sales,
  psTtm: data.ps_ttm,
  peTtm: data.pe_ttm,
  epsTtm: data.eps_ttm,
  averageEps: data.average_eps,
  averagePricePerEps: data.average_price_per_eps,
  earningPerShare: data.earning_per_share,
  pricePerEps: data.price_per_eps,
});

export const normalizeAnalysisData = (
  data: FundamentalFormulaType,
  unit: string,
  changedValues: CommodityItemType[],
): AnalysisType => {
  // if the commodity unit is percent, we should be divided by 100, then send to the backend
  const values = {
    ...data,
    value: unit === i18n.global.percent ? Number(data.value) / 100 : data.value,
  };
  const { id, slug, value } = values;
  const oldData = changedValues
    .map((item) => ({
      value:
        item.unit === i18n.global.percent
          ? Number(item.value) / 100
          : item.value,
      slug: item.slug,
    }))
    // When the user changes the value that has been changed once for the second time. Its previous changes must be deleted
    .filter((item) => item.slug !== slug);

  return [
    {
      id,
      value_slug_list: [
        {
          value,
          slug,
        },
        ...oldData,
      ],
    },
  ];
};
